import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Clock,
  Loader,
  CheckCircle,
  XCircle,
  RotateCcw,
  AlertCircle,
  Eye,
  RefreshCw,
  Download,
} from 'lucide-react';
import {
  useJobs,
  useJobStats,
  useSystemStatus,
  usePauseSending,
  useResumeSending,
  useCancelJob,
  useRequeueJob,
  useBulkCancelJobs,
  useBulkRequeueJobs,
} from '../../../hooks/useJobs';
import { useQueryClient } from '@tanstack/react-query';
import { useTemplates } from '../../../hooks/useTemplates';
import { useRules, usePauseRuleJobs } from '../../../hooks/useRules';
import { formatRelativeTime } from '../../../utils/formatters';
import { useToast } from '../../../contexts/ToastContext';
import JobDetailDrawer from './JobDetailDrawer';

function EmailQueuePage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('All');
  const [templateFilter, setTemplateFilter] = useState('All');
  const [searchFilter, setSearchFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [selectedJobs, setSelectedJobs] = useState(new Set());
  const [autoRefresh, setAutoRefresh] = useState(true); // eslint-disable-line no-unused-vars

  const { templates } = useTemplates({});
  const { rules } = useRules({ isActive: 'true' });
  const { data: statsData } = useJobStats();
  const { data: systemData } = useSystemStatus();
  const pauseMutation = usePauseSending();
  const resumeMutation = useResumeSending();
  const cancelMutation = useCancelJob();
  const requeueMutation = useRequeueJob();
  const bulkCancelMutation = useBulkCancelJobs();
  const bulkRequeueMutation = useBulkRequeueJobs();
  const pauseRuleMutation = usePauseRuleJobs();

  const stats = statsData?.data || {};
  const systemStatus = systemData?.data || {};

  // Build filters for jobs query
  const filters = { page, limit: 25 };
  if (statusFilter !== 'All') filters.status = statusFilter;
  if (templateFilter !== 'All') filters.templateId = templateFilter;
  if (searchFilter) filters.search = searchFilter;
  if (fromDate) filters.from = fromDate;
  if (toDate) filters.to = toDate;

  const { data: jobsData, isLoading, error } = useJobs(filters);
  const jobs = useMemo(() => jobsData?.data || [], [jobsData]);
  const total = jobsData?.total || 0;
  const totalPages = Math.ceil(total / 25);

  const handleStatClick = useCallback((status) => {
    setStatusFilter(status);
    setPage(1);
  }, []);

  const handlePauseToggle = useCallback(async () => {
    const isPaused = systemStatus.sendingPaused;
    if (!isPaused) {
      if (!window.confirm('This will stop ALL email sending. Are you sure?')) {
        return;
      }
    }

    try {
      if (isPaused) {
        await resumeMutation.mutateAsync();
        toast.success('Email sending resumed');
      } else {
        await pauseMutation.mutateAsync();
        toast.success('Email sending paused');
      }
    } catch (err) {
      toast.error('Failed to toggle sending state');
    }
  }, [systemStatus.sendingPaused, pauseMutation, resumeMutation, toast]);

  const handleCancelJob = useCallback(
    async (e, jobId) => {
      e.stopPropagation();
      if (!window.confirm('Cancel this job?')) return;

      try {
        await cancelMutation.mutateAsync(jobId);
        toast.success('Job canceled');
      } catch (err) {
        toast.error(err.message || 'Failed to cancel job');
      }
    },
    [cancelMutation, toast]
  );

  const handleRequeueJob = useCallback(
    async (e, jobId) => {
      e.stopPropagation();
      if (!window.confirm('Requeue this job?')) return;

      try {
        await requeueMutation.mutateAsync(jobId);
        toast.success('Job requeued');
      } catch (err) {
        toast.error(err.message || 'Failed to requeue job');
      }
    },
    [requeueMutation, toast]
  );

  const handleResetFilters = useCallback(() => {
    setStatusFilter('All');
    setTemplateFilter('All');
    setSearchFilter('');
    setFromDate('');
    setToDate('');
    setPage(1);
  }, []);

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['email-jobs'] });
    queryClient.invalidateQueries({ queryKey: ['email-job-stats'] });
    toast.success('Refreshed');
  }, [queryClient, toast]);

  const handleExportCSV = useCallback(() => {
    if (jobs.length === 0) {
      toast.error('No jobs to export');
      return;
    }

    const headers = ['Job ID', 'Status', 'Recipient', 'Template', 'Send At', 'Created At', 'Error'];
    const rows = jobs.map((job) => [
      job.id,
      job.status,
      job.customer_email || '',
      job.template_name || '',
      new Date(job.send_at).toISOString(),
      new Date(job.created_at).toISOString(),
      job.last_error || '',
    ]);

    const csvContent = [headers, ...rows].map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `email-jobs-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);

    toast.success('CSV exported');
  }, [jobs, toast]);

  const handleToggleSelection = useCallback((jobId) => {
    setSelectedJobs((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) {
        next.delete(jobId);
      } else {
        next.add(jobId);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedJobs.size === jobs.length && jobs.length > 0) {
      setSelectedJobs(new Set());
    } else {
      setSelectedJobs(new Set(jobs.map((j) => j.id)));
    }
  }, [jobs, selectedJobs.size]);

  const handleClearSelection = useCallback(() => {
    setSelectedJobs(new Set());
  }, []);

  const handleBulkCancel = useCallback(async () => {
    const jobIds = Array.from(selectedJobs);
    if (!window.confirm(`Cancel ${jobIds.length} selected jobs?`)) return;

    try {
      await bulkCancelMutation.mutateAsync({ jobIds });
      toast.success(`${jobIds.length} jobs canceled`);
      setSelectedJobs(new Set());
    } catch (err) {
      toast.error('Failed to cancel jobs');
    }
  }, [selectedJobs, bulkCancelMutation, toast]);

  const handleBulkRequeue = useCallback(async () => {
    const jobIds = Array.from(selectedJobs);
    if (!window.confirm(`Requeue ${jobIds.length} selected jobs?`)) return;

    try {
      await bulkRequeueMutation.mutateAsync(jobIds);
      toast.success(`${jobIds.length} jobs requeued`);
      setSelectedJobs(new Set());
    } catch (err) {
      toast.error('Failed to requeue jobs');
    }
  }, [selectedJobs, bulkRequeueMutation, toast]);

  const handlePauseRule = useCallback(
    async (ruleId, ruleName, queuedCount) => {
      if (
        !window.confirm(
          `This will cancel ${queuedCount} queued jobs and deactivate "${ruleName}". Continue?`
        )
      )
        return;

      try {
        const result = await pauseRuleMutation.mutateAsync(ruleId);
        toast.success(
          `Rule paused. ${result.data.canceled} jobs canceled and rule deactivated.`
        );
      } catch (err) {
        toast.error(err.message || 'Failed to pause rule');
      }
    },
    [pauseRuleMutation, toast]
  );

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore if input is focused
      const target = e.target;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
        return;
      }

      // Escape: close drawer or clear selection
      if (e.key === 'Escape') {
        if (selectedJobId) {
          setSelectedJobId(null);
        } else if (selectedJobs.size > 0) {
          setSelectedJobs(new Set());
        }
      }

      // r: requeue selected
      if (e.key === 'r' && selectedJobs.size > 0) {
        handleBulkRequeue();
      }

      // c: cancel selected
      if (e.key === 'c' && selectedJobs.size > 0) {
        handleBulkCancel();
      }

      // /: focus search
      if (e.key === '/') {
        e.preventDefault();
        const searchInput = document.querySelector('.email-search-input');
        if (searchInput) searchInput.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedJobId, selectedJobs, handleBulkCancel, handleBulkRequeue]);

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'queued':
        return 'queue-status-queued';
      case 'processing':
        return 'queue-status-processing';
      case 'sent':
        return 'queue-status-sent';
      case 'failed':
        return 'queue-status-failed';
      case 'canceled':
        return 'queue-status-canceled';
      default:
        return '';
    }
  };

  const formatSendAt = (sendAt) => {
    const date = new Date(sendAt);
    const now = new Date();
    if (date > now) {
      return formatRelativeTime(sendAt);
    }
    return date.toLocaleString();
  };

  return (
    <div className="email-page">
      <div className="email-page-header">
        <h3>Email Queue</h3>
      </div>

      {/* Stats Bar */}
      <div className="queue-stats-bar">
        <div
          className="queue-stat-card queue-stat-queued"
          onClick={() => handleStatClick('queued')}
        >
          <Clock size={24} />
          <div className="queue-stat-value">{stats.queued || 0}</div>
          <div className="queue-stat-label">Queued</div>
        </div>
        <div
          className="queue-stat-card queue-stat-processing"
          onClick={() => handleStatClick('processing')}
        >
          <Loader size={24} />
          <div className="queue-stat-value">{stats.processing || 0}</div>
          <div className="queue-stat-label">Processing</div>
        </div>
        <div
          className="queue-stat-card queue-stat-sent"
          onClick={() => handleStatClick('sent')}
        >
          <CheckCircle size={24} />
          <div className="queue-stat-value">{stats.sentToday || 0}</div>
          <div className="queue-stat-label">Sent Today</div>
        </div>
        <div
          className="queue-stat-card queue-stat-failed"
          onClick={() => handleStatClick('failed')}
        >
          <XCircle size={24} />
          <div className="queue-stat-value">{stats.failedToday || 0}</div>
          <div className="queue-stat-label">Failed Today</div>
        </div>
        <div className="queue-stat-card queue-stat-next">
          <Clock size={24} />
          <div className="queue-stat-value">
            {stats.nextDueAt ? formatRelativeTime(stats.nextDueAt) : 'None'}
          </div>
          <div className="queue-stat-label">Next Send</div>
        </div>
      </div>

      {/* Global Controls */}
      <div className="queue-controls-bar">
        <button
          className={`btn ${systemStatus.sendingPaused ? 'btn-success' : 'btn-danger'}`}
          onClick={handlePauseToggle}
          disabled={pauseMutation.isPending || resumeMutation.isPending}
        >
          {systemStatus.sendingPaused ? 'Resume Sending' : 'Pause Sending'}
        </button>
        <span className="queue-last-run">
          Queue Runner (runs hourly) — Last Run:{' '}
          {systemStatus.queueRunnerLastRun
            ? formatRelativeTime(systemStatus.queueRunnerLastRun)
            : 'Never'}
        </span>
      </div>

      {/* Pause Banner */}
      {systemStatus.sendingPaused && (
        <div className="queue-pause-banner">
          <AlertCircle size={18} />
          Sending is paused. No emails will be delivered until resumed.
        </div>
      )}

      {/* Filters */}
      <div className="email-filter-bar">
        <select
          className="email-filter-select"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="All">All Status</option>
          <option value="queued">Queued</option>
          <option value="processing">Processing</option>
          <option value="sent">Sent</option>
          <option value="failed">Failed</option>
          <option value="canceled">Canceled</option>
        </select>

        <select
          className="email-filter-select"
          value={templateFilter}
          onChange={(e) => {
            setTemplateFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="All">All Templates</option>
          {templates.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>

        <input
          type="date"
          className="email-filter-select"
          value={fromDate}
          onChange={(e) => {
            setFromDate(e.target.value);
            setPage(1);
          }}
          placeholder="From"
        />

        <input
          type="date"
          className="email-filter-select"
          value={toDate}
          onChange={(e) => {
            setToDate(e.target.value);
            setPage(1);
          }}
          placeholder="To"
        />

        <input
          type="text"
          className="email-search-input"
          placeholder="Search email or reservation ID..."
          value={searchFilter}
          onChange={(e) => {
            setSearchFilter(e.target.value);
            setPage(1);
          }}
        />

        <button className="btn btn-secondary" onClick={handleResetFilters}>
          Reset
        </button>
        <button className="btn btn-secondary" onClick={handleRefresh} title="Refresh (F5)">
          <RefreshCw size={16} />
          Refresh
        </button>
        <button className="btn btn-secondary" onClick={handleExportCSV} title="Export to CSV">
          <Download size={16} />
          Export CSV
        </button>
      </div>

      {/* Bulk Action Bar */}
      {selectedJobs.size > 0 && (
        <div className="queue-bulk-bar">
          <span className="queue-bulk-count">{selectedJobs.size} jobs selected</span>
          <div className="queue-bulk-actions">
            {jobs.some((j) => selectedJobs.has(j.id) && j.status === 'queued') && (
              <button
                className="btn btn-danger"
                onClick={handleBulkCancel}
                title="Cancel selected (c)"
              >
                Cancel Selected
              </button>
            )}
            {jobs.some(
              (j) =>
                selectedJobs.has(j.id) && (j.status === 'failed' || j.status === 'canceled')
            ) && (
              <button
                className="btn btn-primary"
                onClick={handleBulkRequeue}
                title="Requeue selected (r)"
              >
                Requeue Selected
              </button>
            )}
            <button className="btn btn-secondary" onClick={handleClearSelection}>
              Clear Selection
            </button>
          </div>
        </div>
      )}

      {/* Rule-Level Actions */}
      {rules && rules.length > 0 && (
        <details className="queue-rule-actions">
          <summary className="queue-rule-summary">Pause Rules</summary>
          <div className="queue-rule-list">
            {rules.map((rule) => (
              <div key={rule.id} className="queue-rule-item">
                <span className="queue-rule-name">
                  {rule.name} ({rule.queued_count || 0} queued)
                </span>
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={() => handlePauseRule(rule.id, rule.name, rule.queued_count || 0)}
                >
                  Pause Rule
                </button>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* Error State */}
      {error && (
        <div className="email-error">
          Failed to load jobs. Please try again.
        </div>
      )}

      {/* Loading State */}
      {isLoading ? (
        <div className="email-table-skeleton">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="skeleton-row" />
          ))}
        </div>
      ) : jobs.length === 0 ? (
        <div className="email-empty-state">
          <Clock size={48} strokeWidth={1} />
          <p>
            {statusFilter !== 'All' ||
            templateFilter !== 'All' ||
            searchFilter ||
            fromDate ||
            toDate
              ? 'No jobs match your filters.'
              : 'No email jobs yet. Jobs will appear here when automation rules trigger or manual sends are initiated.'}
          </p>
          {(statusFilter !== 'All' ||
            templateFilter !== 'All' ||
            searchFilter ||
            fromDate ||
            toDate) && (
            <button className="btn btn-secondary" onClick={handleResetFilters}>
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="email-table-wrapper">
            <table className="email-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={selectedJobs.size === jobs.length && jobs.length > 0}
                      onChange={handleSelectAll}
                      title="Select all"
                    />
                  </th>
                  <th>Status</th>
                  <th>Recipient</th>
                  <th>Template</th>
                  <th>Subject</th>
                  <th>Send At</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <tr key={job.id} className="email-table-row">
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedJobs.has(job.id)}
                        onChange={() => handleToggleSelection(job.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td>
                      <span className={`queue-status-badge ${getStatusBadgeClass(job.status)}`}>
                        {job.status}
                      </span>
                    </td>
                    <td>{job.customer_email || 'Unknown'}</td>
                    <td>
                      {job.template_name ? (
                        <Link to={`/admin/email/templates/${job.template_id}`}>
                          {job.template_name}
                        </Link>
                      ) : (
                        '(deleted)'
                      )}
                    </td>
                    <td className="email-table-subject">
                      {job.payload_snapshot?.subject?.slice(0, 50) || 'N/A'}
                      {job.payload_snapshot?.subject?.length > 50 && '...'}
                    </td>
                    <td>{formatSendAt(job.send_at)}</td>
                    <td>{formatRelativeTime(job.created_at)}</td>
                    <td className="queue-actions">
                      {job.status === 'queued' && (
                        <button
                          className="queue-action-btn"
                          title="Cancel"
                          onClick={(e) => handleCancelJob(e, job.id)}
                        >
                          <XCircle size={16} />
                        </button>
                      )}
                      {(job.status === 'failed' || job.status === 'canceled') && (
                        <button
                          className="queue-action-btn"
                          title="Requeue"
                          onClick={(e) => handleRequeueJob(e, job.id)}
                        >
                          <RotateCcw size={16} />
                        </button>
                      )}
                      {job.status === 'failed' && job.last_error && (
                        <button
                          className="queue-action-btn"
                          title={job.last_error}
                        >
                          <AlertCircle size={16} />
                        </button>
                      )}
                      <button
                        className="queue-action-btn"
                        title="View Details"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedJobId(job.id);
                        }}
                      >
                        <Eye size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="email-table-footer">
            <div>
              {total} job{total !== 1 ? 's' : ''}
            </div>
            {totalPages > 1 && (
              <div className="queue-pagination">
                <button
                  className="btn btn-secondary"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </button>
                <span>
                  Page {page} of {totalPages}
                </span>
                <button
                  className="btn btn-secondary"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Job Detail Drawer */}
      {selectedJobId && (
        <JobDetailDrawer jobId={selectedJobId} onClose={() => setSelectedJobId(null)} />
      )}
    </div>
  );
}

export default EmailQueuePage;
